<script setup lang="ts">
// The disease week the population question is read at. Choosing a week crops the
// whole view — the cell field, the class breaks, the header readings and the
// chart axis all stop at that week — so the control sits above the map rather
// than inside one panel: it moves everything beneath it.
//
// The selection is a *choice*, not a resolved answer. The payload already names
// the season and the week its readings sit at (the latest week with an analysed
// sample, unless a week was asked for), so this row only has to say which of
// those the view is showing and report a change. Nothing here derives a window:
// the server decides what a week means for a season it has fewer samples in.
import { computed } from "vue";

import type { MapReviewWeek } from "@/lib/mapReviewState";

const props = defineProps<{
	year: number; // payload.year
	lastWeek: number; // payload.lastWeek — where the readings sit
	seasonFirst: number;
	seasonLast: number;
	seasons: number[]; // ascending; the season select lists them newest first
	selection: MapReviewWeek | null;
	filtered: boolean;
	loading: boolean;
	range: { from: string | null; to: string | null };
}>();

const emit = defineEmits<{
	(e: "select", value: MapReviewWeek | null): void;
}>();

/** The season the view is drawn for: the operator's choice, else the payload's.
 * While a filter is in force the choice is not what was read — the server covers
 * the filtered set's own span — so the payload's own season is what the row
 * states, exactly as the caption below it does. */
const displayYear = computed(() =>
	props.filtered ? props.year : (props.selection?.year ?? props.year),
);

/** The week the readings are taken at: the operator's choice, else the payload's,
 * and the payload's alone while a filter overrides the choice. */
const displayWeek = computed(() =>
	props.filtered ? props.lastWeek : (props.selection?.week ?? props.lastWeek),
);

/**
 * The seasons on offer, newest first.
 *
 * The displayed season is added when the payload's list does not carry it: an
 * empty `seasons` means no season has an analysed sample, which is a season the
 * view may still be drawn for — and a select whose value names no option shows
 * the first option's label instead, silently misnaming what is on screen.
 */
const seasonOptions = computed(() => {
	const years = new Set(props.seasons);
	years.add(displayYear.value);
	return [...years].sort((a, b) => b - a);
});

/**
 * The weeks on offer, ascending.
 *
 * The season's analysable band is `seasonFirst..seasonLast` (both 0 when the
 * season carries no analysed sample), and the displayed week is added because a
 * week past the last sample is an honest, empty week the server still draws —
 * without it the select would fall back to its first option and claim a week the
 * readings are not taken at.
 */
const weekOptions = computed(() => {
	const weeks = new Set<number>();
	if (props.seasonFirst > 0) {
		for (let week = props.seasonFirst; week <= props.seasonLast; week++) {
			weeks.add(week);
		}
	}
	weeks.add(displayWeek.value);
	return [...weeks].sort((a, b) => a - b);
});

const disabled = computed(() => props.filtered || props.loading);

const rowTitle = computed(() =>
	props.filtered
		? "A filter is in force: the view reads the filtered set's own date span, so the week and season cannot be set"
		: "The disease week the whole view is read at — the field, the breaks, the readings and the chart axis all stop here",
);

/**
 * Report a pair.
 *
 * The payload's own pair is the default, and emitting it as a selection would
 * pin the store and the URL to a choice nobody made: a reload would then keep a
 * week that was only ever the newest sample, and a link taken before any choice
 * would open narrowed. So the default emits `null` and the two-tier state stays
 * untouched.
 */
function emitPair(year: number, week: number): void {
	if (year === props.year && week === props.lastWeek) emit("select", null);
	else emit("select", { year, week });
}

/**
 * A new season keeps the displayed week.
 *
 * Only the displayed season's analysable band is known, so a week that exists in
 * the picked season cannot be looked up here — and clamping against the wrong
 * band would invent one. The server honours any week 1..53 and draws the weeks a
 * season has no sample in as empty, which is the same honest answer the operator
 * already gets by choosing a week past the last sample.
 */
function onSeasonInput(event: Event): void {
	const year = Number((event.target as HTMLSelectElement).value);
	emitPair(year, displayWeek.value);
}

function onWeekInput(event: Event): void {
	const week = Number((event.target as HTMLSelectElement).value);
	emitPair(displayYear.value, week);
}

/**
 * A calendar day, formatted where the day was written.
 *
 * The server writes sampling days in UTC, so both the parse and the format pin
 * UTC: parsed as local midnight the day would slide to the previous one in any
 * zone west of Greenwich, and a range would start on a day it does not reach.
 */
function dayLabel(day: string, withYear: boolean): string {
	return new Date(`${day}T00:00:00Z`).toLocaleDateString(
		"en-US",
		withYear
			? { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" }
			: { month: "short", day: "numeric", timeZone: "UTC" },
	);
}

function dayYear(day: string): number {
	return new Date(`${day}T00:00:00Z`).getUTCFullYear();
}

/**
 * The days the drawn window covers, appended to what the window is.
 *
 * The year is stated once when both ends share it, and on both ends when the
 * window crosses a new year — `Dec 20 – Jan 3` would hide the turn. A window
 * with only one end names no span, so the label stands alone rather than
 * claiming a range half the server did not state.
 */
function captionOf(label: string): string {
	const from = props.range.from;
	const to = props.range.to;
	if (from === null || to === null) return label;
	const days =
		dayYear(from) === dayYear(to)
			? `${dayLabel(from, false)} – ${dayLabel(to, true)}`
			: `${dayLabel(from, true)} – ${dayLabel(to, true)}`;
	return `${label} · ${days}`;
}

/**
 * The line under the row, and null while the view is the untouched default: the
 * default is what the panel already says it is, so naming it would be a caption
 * on every opening of the report. A filter is stated even with no selection,
 * because the filter is what decides the span then.
 */
const caption = computed(() => {
	if (props.filtered) return captionOf("Filtered set");
	if (props.selection === null) return null;
	return captionOf(`Week ${displayWeek.value}`);
});
</script>

<template>
	<div class="population-week">
		<div class="population-week-row" :title="rowTitle">
			<label class="population-week-field">
				Season
				<select
					class="population-week-select"
					:value="displayYear"
					:disabled="disabled"
					aria-label="Season"
					@change="onSeasonInput"
				>
					<option v-for="year in seasonOptions" :key="year" :value="year">
						{{ year }}
					</option>
				</select>
			</label>
			<label class="population-week-field">
				Week
				<select
					class="population-week-select"
					:value="displayWeek"
					:disabled="disabled"
					aria-label="Week"
					@change="onWeekInput"
				>
					<option v-for="week in weekOptions" :key="week" :value="week">
						Week {{ week }}
					</option>
				</select>
			</label>
		</div>
		<p v-if="caption" class="population-week-caption">{{ caption }}</p>
	</div>
</template>

<style scoped>
/* The pane's slot is a cell grid; this row is the frame every panel below it is
   read in, so it spans them rather than taking a cell of its own. */
.population-week {
	grid-column: 1 / -1;
	min-width: 0;
}

.population-week-row {
	display: flex;
	align-items: center;
	justify-content: flex-end;
	gap: 10px;
	font-size: 0.72rem;
	color: #6c757d;
}

.population-week-field {
	display: inline-flex;
	align-items: center;
	gap: 4px;
}

.population-week-select {
	font-size: 0.72rem;
	color: #495057;
	border: 1px solid #dee2e6;
	border-radius: 3px;
	background: #fff;
	padding: 1px 4px;
	box-shadow: none;
}

.population-week-select:disabled {
	opacity: 0.6;
	cursor: default;
}

.population-week-caption {
	margin: 3px 0 0;
	text-align: right;
	font-size: 0.68rem;
	color: #6c757d;
}
</style>
